import { useEffect, useState, useSyncExternalStore } from "react";
import { Check, Loader2, Pause, Play, VolumeX } from "lucide-react";
import {
  MAIL_NOTIFICATION_SOUND_OPTIONS,
  persistMailNotificationSoundId,
  readMailNotificationSoundId,
  type MailNotificationSoundId,
} from "@/lib/mail/mailNotificationSoundPrefs";
import {
  getMailNotificationSoundPlayingId,
  getMailNotificationSoundPreloadProgress,
  isMailNotificationSoundReady,
  preloadMailNotificationSounds,
  subscribeMailNotificationSoundPlayback,
  subscribeMailNotificationSoundPreload,
  toggleMailNotificationSoundPreview,
} from "@/lib/mail/mailNotificationSound";
import { cn } from "@/lib/cn";

type Props = {
  onPick?: () => void;
};

function useSoundPreloadProgress() {
  return useSyncExternalStore(
    subscribeMailNotificationSoundPreload,
    getMailNotificationSoundPreloadProgress,
    getMailNotificationSoundPreloadProgress
  );
}

function useSoundPlayingId() {
  return useSyncExternalStore(
    subscribeMailNotificationSoundPlayback,
    getMailNotificationSoundPlayingId,
    getMailNotificationSoundPlayingId
  );
}

/** Choix du son de notification + écoute. */
export function MailNotificationSoundPanel({ onPick }: Props) {
  const [selected, setSelected] = useState<MailNotificationSoundId>(() =>
    readMailNotificationSoundId()
  );
  const [loadingId, setLoadingId] = useState<MailNotificationSoundId | null>(null);
  const progress = useSoundPreloadProgress();
  const playingId = useSoundPlayingId();

  useEffect(() => {
    void preloadMailNotificationSounds();
  }, []);

  async function previewSound(id: MailNotificationSoundId) {
    if (id === "off") return;
    const showSpinner = !isMailNotificationSoundReady(id) && playingId !== id;
    if (showSpinner) setLoadingId(id);
    try {
      await toggleMailNotificationSoundPreview(id);
    } catch {
      /* fichier indisponible / autoplay */
    } finally {
      setLoadingId((current) => (current === id ? null : current));
    }
  }

  function selectSound(id: MailNotificationSoundId) {
    setSelected(id);
    persistMailNotificationSoundId(id);
    if (id !== "off") {
      void previewSound(id);
    }
    onPick?.();
  }

  return (
    <div className="space-y-2">
      <p className="px-0.5 text-[11px] leading-snug text-[var(--mail-muted)]">
        Son joué à l’arrivée d’un nouveau message.
      </p>

      <div
        className="rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface-muted)] px-2.5 py-2"
        role="status"
        aria-live="polite"
        aria-busy={!progress.complete}
      >
        <div className="mb-1.5 flex items-center justify-between gap-2 text-[11px]">
          <span className="font-medium text-[var(--mail-text)]">
            {progress.complete ? "Sons prêts" : "Chargement des sons…"}
          </span>
          <span className="tabular-nums text-[var(--mail-muted)]">
            {progress.done}/{progress.total}
          </span>
        </div>
        <div
          className="h-1.5 overflow-hidden rounded-full bg-[var(--mail-border-soft)]"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress.percent}
          aria-label="Progression du chargement des sons"
        >
          <div
            className={cn(
              "h-full rounded-full bg-[var(--mail-accent)] transition-[width] duration-300 ease-out",
              !progress.complete && progress.done === 0 && "inbox-load-progress-indeterminate w-2/5"
            )}
            style={
              progress.complete || progress.done > 0
                ? { width: `${Math.max(progress.percent, progress.done > 0 ? 8 : 0)}%` }
                : undefined
            }
          />
        </div>
      </div>

      <ul className="space-y-0.5" role="listbox" aria-label="Son de notification">
        {MAIL_NOTIFICATION_SOUND_OPTIONS.map((option) => {
          const isSelected = selected === option.id;
          const isOff = option.id === "off";
          const isLoading = loadingId === option.id;
          const isPlaying = playingId === option.id;
          return (
            <li key={option.id} className="flex items-center gap-0.5">
              <button
                type="button"
                role="option"
                aria-selected={isSelected}
                className={cn(
                  "flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition",
                  isSelected
                    ? "bg-[color-mix(in_srgb,var(--mail-accent)_12%,transparent)] text-[var(--mail-text)]"
                    : "text-[var(--mail-text)] hover:bg-[var(--mail-surface-hover)]"
                )}
                onClick={() => selectSound(option.id)}
              >
                <span
                  className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
                    isSelected
                      ? "border-[var(--mail-accent)] bg-[var(--mail-accent)] text-white"
                      : "border-[var(--mail-border)]"
                  )}
                  aria-hidden
                >
                  {isSelected ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : null}
                </span>
                <span className="min-w-0 flex-1 truncate font-medium">{option.label}</span>
                {isOff ? (
                  <VolumeX className="h-3.5 w-3.5 shrink-0 text-[var(--mail-muted)]" aria-hidden />
                ) : null}
              </button>
              {!isOff ? (
                <button
                  type="button"
                  className={cn(
                    "shrink-0 rounded-lg p-2 transition disabled:opacity-60",
                    isPlaying
                      ? "text-[var(--mail-accent)] hover:bg-[var(--mail-accent-soft)]"
                      : "text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-accent)]"
                  )}
                  aria-label={
                    isLoading
                      ? `Chargement de ${option.label}`
                      : isPlaying
                        ? `Pause ${option.label}`
                        : `Écouter ${option.label}`
                  }
                  aria-busy={isLoading}
                  aria-pressed={isPlaying}
                  title={isLoading ? "Chargement…" : isPlaying ? "Pause" : "Écouter"}
                  disabled={isLoading}
                  onClick={(event) => {
                    event.stopPropagation();
                    void previewSound(option.id);
                  }}
                >
                  {isLoading ? (
                    <Loader2
                      className="h-3.5 w-3.5 animate-spin text-[var(--mail-accent)]"
                      strokeWidth={2.25}
                      aria-hidden
                    />
                  ) : isPlaying ? (
                    <Pause className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
                  ) : (
                    <Play className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
                  )}
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
